import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteLocationRaw
} from 'vue-router'
import i18nHelpers from '@/i18n/i18nHelpers'
import { br } from '@/plugins/trackerPlugin'
import { getInfoLibraryRoutes } from '@/pages/infoLibrary/config/library.routes'
import { getJournalRoutes } from '@/pages/journal/config/journal.routes'
import { getCalendarRoutes } from '@/pages/calendar/config/calendar.routes'
import { getSignupRoutes } from '@/pages/signup/config/signup.routes'
import { getFeeWaiverRoutes } from '@/pages/feeWaiver/config/feeWaiver.routes'
const ContactUsLayout = () => import('@/layouts/ContactUsLayout.vue')
const NotFoundLayout = () => import('@/layouts/NotFoundLayout.vue')
const LandingPageLayout = () => import('@/layouts/LandingPageLayout.vue')

import LoginPage from '@/pages/login/LoginPage.vue'
const LogoutPage = () => import('@/pages/login/LogoutPage.vue')
import ForgotEmailOrPasswordVue from '@/pages/login/ForgotEmailOrPassword.vue'
import ResetPassword from '@/pages/login/ResetPassword.vue'
const verifyEmailChange = () => import('@/pages/accounts/VerifyEmail.vue')

const AccountSettings = () => import('@/pages/accounts/AccountSettings.vue')
const GeneralInfo = () => import('@/pages/accounts/GeneralInfo.vue')
const PlanDetails = () => import('@/pages/accounts/PlanDetails.vue')
const Notifications = () => import('@/pages/accounts/Notifications.vue')
const Password = () => import('@/pages/accounts/Password.vue')
const AccountableCalling = () =>
  import('@/pages/accounts/AccountableCalling.vue')
const AccountTypeStep1 = () => import('@/pages/accounts/AccountTypeStep1.vue')
const SecureMessaging = () => import('@/pages/accounts/SecureMessaging.vue')

const ContactUs = () => import('@/pages/contact-us/ContactUsPage.vue')

import { useLoginStore } from '@/stores/LoginStore'
import { useCommonStore } from '@/stores/CommonStore'
import { useAccountSettingsStore } from '@/stores/AccountSettingsStore'
import { useCallingStore } from '@/stores/CallingStore'
import { Settings } from 'luxon'

import decodeJwtService from '@/services/decodeJwtService'
const AccountSettingsTabs = () =>
  import('@/pages/accounts/AccountSettingsTabs.vue')
const EditName = () => import('@/pages/accounts/components/editName.vue')
const SettingsEditPhoneNumber = () =>
  import('@/pages/accounts/components/SettingsEditPhoneNumber.vue')
const EditEmail = () => import('@/pages/accounts/components/editEmail.vue')
const EditDateTimeSettings = () =>
  import('@/pages/accounts/components/editDateTimeSettings.vue')
import Appearance from '@/pages/accounts/Appearance.vue'
import { loadLocales } from '@/utils/i18n.utils'
import {
  getLoginRoute,
  getMatchedHomeRoute,
  getRestrictedPaywallRoute,
  hasUserActivity,
  isSectionAllowedForUser,
  type AppSection
} from '@/router/accessPolicy'

import { checkCalendar3ColumnFeatureFlags } from './routerHelpers'

const NotFound = () => import('@/pages/notFound/NotFound.vue')
const NewsletterSubscribe = () =>
  import('@/pages/subscribe/NewsletterSignup.vue')

const ShareVault = () => import('@/pages/vault/ShareVault.vue')

const Calling = () => import('@/pages/calling/Calling.vue')
const VideoCall = () => import('@/pages/calling/VideoCall.vue')
const PdfDownload = () => import('@/pages/pdfDownload/PdfDownload.vue')
const NewsletterWelcome = () =>
  import('@/pages/subscribe/NewsletterWelcome.vue')

const PostMatchLayout = () => import('@/layouts/PostMatchLayout.vue')
const PostMatch = () => import('@/pages/postMatch/PostMatchPage.vue')

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      name: 'apple-app-site-association',
      path: '/.well-known/apple-app-site-association',
      component: LandingPageLayout,
      meta: { requiresAuth: false, requiresMatched: false },
      beforeEnter: () => {
        window.location.assign('/.well-known/apple-app-site-association')
        return false
      }
    },
    { path: '/moneyTransfer', redirect: { name: 'moneyTransfer' } },
    { path: '/', redirect: getMatchedHomeRoute() },
    {
      path: '/minSuccess/:sessionId',
      redirect: (to) => {
        const callingStore = useCallingStore()
        const accountSettings = useAccountSettingsStore()

        // record
        const sessionId =
          to.params.sessionId instanceof Array
            ? to.params.sessionId[0]
            : to.params.sessionId || ''
        accountSettings.addMinSuccess(sessionId)
        accountSettings.setShowAddMinutesSuccessToast(true)

        // show modals when needed
        if (to.query.video === 'true') {
          callingStore.tryShowVideoCall()
        }

        if (to.query.audio === 'true') {
          callingStore.setShowAudioCall(true)
        }

        // send to prev route
        if (to.query.p) {
          if (to.query.p.includes('calendar/entry')) {
            return { name: 'calendar', query: {} }
          } else {
            return { path: `${to.query.p}`, query: {} }
          }
        } else {
          return { name: 'accountableCalling', query: {} }
        }
      }
    },
    {
      path: '/minCancel/:sessionId',
      redirect: (to) => {
        const callingStore = useCallingStore()
        const accountSettings = useAccountSettingsStore()

        // cancel
        const sessionId =
          to.params.sessionId instanceof Array
            ? to.params.sessionId[0]
            : to.params.sessionId || ''
        accountSettings.cancelProcessing(sessionId)

        // show modals when needed
        if (to.query.video === 'true') {
          callingStore.tryShowVideoCall()
        }

        if (to.query.audio === 'true') {
          callingStore.setShowAudioCall(true)
        }

        // send to prev route
        if (to.query.p) {
          if (to.query.p.includes('calendar/entry')) {
            return { name: 'calendar', query: {} }
          } else {
            return { path: `${to.query.p}`, query: {} }
          }
        } else {
          return { name: 'accountableCalling', query: {} }
        }
      }
    },
    {
      path: '/legal/subpoena',
      alias: [
        '/legal/subpoenaRecordRequestConfirmation',
        '/legal/subpoenaPayment',
        '/legal/subpoenaRecordRequestPaymentConfirmation'
      ],
      redirect: () => {
        window.location.replace('https://legal.talkingparents.com/subpoenas')
        return {}
      }
    },
    getSignupRoutes(),
    {
      path: '/',
      component: () => import('@/layouts/AuthLayout.vue'),
      beforeEnter: async () => await loadLocales('accountSettings'),
      children: [
        {
          name: 'login',
          path: '/login',
          component: LoginPage,
          meta: { requiresAuth: false, requiresMatched: false }
        },
        {
          name: 'forgotEmailOrPassword',
          path: '/forgotEmailOrPassword',
          component: ForgotEmailOrPasswordVue,
          meta: { requiresAuth: false, requiresMatched: false }
        },
        {
          name: 'resetPassword',
          path: 'reset-password',
          component: ResetPassword,
          meta: { requiresAuth: false, requiresMatched: false }
        },
        {
          name: 'resetPasswordEmail',
          path: 'reset-password/:email',
          component: ResetPassword,
          meta: { requiresAuth: false, requiresMatched: false }
        },
        {
          name: 'logout',
          path: 'logout',
          alias: '',
          component: LogoutPage,
          meta: { requiresAuth: false, requiresMatched: false }
        },
        {
          name: 'logoutClearMfa',
          path: 'logout/:clearMfa?',
          alias: '',
          component: LogoutPage,
          meta: { requiresAuth: false, requiresMatched: false }
        },
        {
          name: 'verifyEmailChange',
          path: 'verifyEmailChange',
          component: verifyEmailChange,
          props: true,
          meta: { requiresAuth: false }
        }
      ]
    },
    {
      path: '/',
      component: async () => await import('@/layouts/TPLayout.vue'),
      beforeEnter: async () => await loadLocales('messages', 'calendar'),
      children: [
        {
          name: 'accountSettingsLayout',
          path: 'accountSettings',
          alias: '',
          component: () => import('@/layouts/AccountSettingsLayout.vue'),
          beforeEnter: async () =>
            await loadLocales('accountSettings', 'calling'),
          props: true,
          meta: {
            requiresAuth: true,
            requiresMatched: true,
            trackingName: br.appActionFeature.settings,
            section: 'accountSettings'
          },
          children: [
            {
              name: 'accountSettingsHome',
              path: '/accountSettings/',
              component: AccountSettingsTabs,
              props: true,
              redirect: { name: 'generalInfo' },
              meta: { requiresAuth: true },
              children: [
                {
                  name: 'generalInfo',
                  path: '/accountSettings/generalInfo',
                  component: GeneralInfo,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'appearance',
                  path: '/accountSettings/appearance',
                  component: Appearance,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'planDetails',
                  path: '/accountSettings/planDetails',
                  component: PlanDetails,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'accountTypeStep1Success',
                  path: '/accountSettings/planDetails/success/:sessionId',
                  component: PlanDetails,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'accountTypeStep1Canceled',
                  path: '/accountSettings/planDetails/canceled/:sessionId',
                  component: PlanDetails,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'payForCoparentSuccess',
                  path: '/accountSettings/planDetails/pfc/success',
                  component: PlanDetails,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'payForCoparentCanceled',
                  path: '/accountSettings/planDetails/pfc/cancel',
                  component: PlanDetails,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'postMatchSubscriptionCanceled',
                  path: '/postMatch/canceled/:sessionId',
                  component: PlanDetails,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'notifications',
                  path: '/accountSettings/notifications',
                  component: Notifications,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'notificationsSmsSuccess',
                  path: '/accountSettings/notifications/success/:sessionId',
                  component: Notifications,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'notificationsSmsCanceled',
                  path: '/accountSettings/notifications/canceled/:sessionId',
                  component: Notifications,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'password',
                  path: '/accountSettings/password',
                  component: Password,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'accountableCalling',
                  path: '/accountSettings/accountableCalling',
                  component: AccountableCalling,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'accountableCallingAddMinSuccess',
                  path: '/accountSettings/accountableCalling/success/:sessionId',
                  component: AccountableCalling,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'accountableCallingAddMinCanceled',
                  path: '/accountSettings/accountableCalling/canceled/:sessionId',
                  component: AccountableCalling,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'secureMessaging',
                  path: '/accountSettings/messages',
                  component: SecureMessaging,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'sharedCalendarTab',
                  path: '/accountSettings/calendar',
                  component: () =>
                    import('@/pages/accounts/AccountSettingsCalendar.vue'),
                  meta: { requiresAuth: true }
                }
              ]
            },
            {
              name: 'accountSettingsFull',
              path: '/accountSettings/',
              component: AccountSettings,
              props: true,
              meta: { requiresAuth: true },
              children: [
                {
                  name: 'editName',
                  path: '/accountSettings/name',
                  component: EditName,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'editPhoneNumber',
                  path: '/accountSettings/phone',
                  component: SettingsEditPhoneNumber,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'editPhoneNumberVerify',
                  path: '/accountSettings/phone/verify',
                  component: SettingsEditPhoneNumber,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'editPhoneNumberEnableMFA',
                  path: '/accountSettings/phone/enable-mfa',
                  component: SettingsEditPhoneNumber,
                  props: { enableMfa: true },
                  meta: { requiresAuth: true }
                },
                {
                  name: 'editEmail',
                  path: '/accountSettings/email',
                  component: EditEmail,
                  props: true,
                  meta: { requiresAuth: true }
                },
                {
                  name: 'editDateTimeSettings',
                  path: '/accountSettings/dateTime',
                  component: EditDateTimeSettings,
                  props: true,
                  meta: { requiresAuth: true }
                }
              ]
            },
            {
              name: 'accountTypeStep1',
              path: '/accountSettings/accountTypeStep1',
              component: AccountTypeStep1,
              props: true,
              meta: { requiresAuth: true }
            }
          ]
        },
        {
          name: 'messagesLayout',
          path: 'messages',
          alias: '',
          component: () => import('@/layouts/MessagesLayout.vue'),
          beforeEnter: async () =>
            await loadLocales('vault', 'records', 'calling', 'payments'),
          props: true,
          meta: {
            requiresAuth: true,
            requiresMatched: true,
            trackingName: br.appActionFeature.messages,
            section: 'messages'
          },
          children: [
            {
              name: 'messages',
              path: '/messages/',
              component: () => import('@/pages/messages/Messages3Column.vue'),
              props: true,
              meta: {
                requiresAuth: true,
                requiresMatched: true,
                layoutContainsAds: false,
                nameForLabels: 'messages.newMessageBtn',
                addNewRouteName: 'messagesEntry'
              }
            },
            {
              name: 'messagesEntry',
              path: '/messages/new',
              redirect: { name: 'messages' }
            },
            {
              name: 'messagesEntryPhoneCall',
              path: '/messages/new/:callLogItemID',
              redirect: { name: 'messages' }
            },
            {
              name: 'messagesEntryVideoCall',
              path: '/messages/new/:videoCallItemId',
              redirect: { name: 'messages' }
            },
            {
              name: 'messagesEntryVoicemail',
              path: '/messages/new/:videoVoicemailItemID',
              redirect: { name: 'messages' }
            },
            {
              name: 'viewMessage',
              path: '/messages/view/:itemId',
              redirect: { name: 'messages' }
            }
          ]
        },
        // Calendar routes - imported from external configuration
        getCalendarRoutes(checkCalendar3ColumnFeatureFlags),
        {
          name: 'moneyTransferLayout',
          path: 'accountablePayments',
          alias: '',
          component: () => import('@/layouts/PaymentsLayout.vue'),
          beforeEnter: async () => await loadLocales('payments'),
          props: false,
          meta: {
            requiresAuth: true,
            trackingName: br.appActionFeature.payments,
            section: 'payments'
          },
          children: [
            {
              name: 'moneyTransferRoot',
              path: '/accountablePayments/',
              component: () =>
                import('@/pages/moneyTransfer/MoneyTransferPage.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              name: 'moneyTransfer',
              path: '/accountablePayments/',
              alias: '/moneyTransfer',
              component: () =>
                import('@/pages/moneyTransfer/MoneyTransferPage.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              name: 'addTransfer',
              path: '/accountablePayments/addTransfer',
              component: () => import('@/pages/moneyTransfer/AddTransfer.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              name: 'addRequest',
              path: '/accountablePayments/addRequest',
              component: () => import('@/pages/moneyTransfer/AddRequest.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              name: 'negativeBalance',
              path: '/accountablePayments/negativeBalance',
              component: () =>
                import('@/pages/moneyTransfer/NegativeBalanceHold.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              name: 'intro',
              path: '/accountablePayments/intro',
              component: () =>
                import('@/pages/moneyTransfer/PaymentsWelcome.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              name: 'mfaDisabled',
              path: '/accountablePayments/mfaDisabled',
              component: () => import('@/pages/moneyTransfer/MfaDisabled.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              name: 'verifyCustomer',
              path: '/accountablePayments/verifyCustomer',
              component: () =>
                import('@/pages/moneyTransfer/VerifyCustomer.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              name: 'verifyMicroDepositPlaid',
              path: '/accountablePayments/verifyMicroDepositPlaid',
              component: () =>
                import('@/pages/moneyTransfer/verifyMicroDepositPlaidComponent.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              name: 'fundingSourceList',
              path: '/accountablePayments/fundingSourceList',
              component: () =>
                import('@/pages/moneyTransfer/PaymentsFundingSourceList.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              name: 'activityList',
              path: '/accountablePayments/activityList',
              component: () => import('@/pages/moneyTransfer/ActivityList.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              name: 'activityDetails',
              path: '/accountablePayments/activityDetails',
              component: () =>
                import('@/pages/moneyTransfer/PaymentsActivityDetails.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              name: 'expressPayIntro',
              path: '/accountablePayments/expressPayIntro',
              component: () =>
                import('@/pages/moneyTransfer/PaymentsExpressPayIntro.vue'),
              props: true,
              meta: { requiresAuth: true }
            }
          ]
        },
        getInfoLibraryRoutes(),
        // Journal routes - imported from external configuration
        getJournalRoutes(),
        {
          name: 'recordsLayout',
          path: 'records',
          alias: '',
          component: () => import('@/layouts/RecordsLayout.vue'),
          beforeEnter: async () => await loadLocales('records'),
          props: true,
          meta: {
            requiresAuth: true,
            requiresMatched: true,
            trackingName: br.appActionFeature.records,
            section: 'records'
          },
          children: [
            {
              name: 'recordsHome',
              path: '/records/',
              component: () => import('@/pages/records/RecordsHome.vue'),
              props: true,
              meta: { requiresAuth: true }
            },
            {
              name: 'recordsMain',
              path: '/records/main',
              component: () => import('@/pages/records/Records.vue'),
              props: (route) => ({ type: route.query.type }),
              meta: { requiresAuth: true }
            },
            {
              name: 'recordsSuccess',
              path: '/records/success',
              component: () => import('@/pages/records/RecordsSuccessPage.vue'),
              props: (route) => ({ type: route.query.type }),
              meta: { requiresAuth: true }
            }
          ]
        },
        {
          name: 'contactUsLayout',
          path: 'contactus',
          alias: '',
          component: ContactUsLayout,
          props: true,
          meta: { requiresAuth: true, trackingName: 'contactus' },
          children: [
            {
              name: 'contactus',
              path: '/contactus/',
              component: ContactUs,
              props: true,
              meta: { requiresAuth: true, layoutContainsAds: false }
            }
          ]
        },
        {
          name: 'vaultLayout',
          path: 'vault',
          alias: '',
          component: () => import('@/layouts/VaultLayout.vue'),
          beforeEnter: async () => await loadLocales('vault'),
          props: true,
          meta: { requiresAuth: true, trackingName: 'vault', section: 'vault' },
          children: [
            {
              name: 'vault',
              path: '/vault/',
              component: () => import('@/pages/vault/Vault.vue'),
              props: true,
              meta: { requiresAuth: true, layoutContainsAds: false }
            },
            {
              name: 'vaultShare',
              path: '/vault/share',
              redirect: { path: '/shareVault' }
            }
          ]
        },
        {
          name: 'callingLayout',
          path: 'calling',
          alias: '',
          component: () => import('@/layouts/CallingLayout.vue'),
          beforeEnter: async () => await loadLocales('calling', 'records'),
          props: true,
          meta: {
            requiresAuth: true,
            trackingName: 'calling',
            section: 'calling'
          },
          children: [
            {
              name: 'calling',
              path: '/calling/',
              component: Calling,
              props: true,
              meta: { requiresAuth: true, layoutContainsAds: false }
            },
            {
              name: 'videocall',
              path: '/calling/videocall',
              component: VideoCall,
              props: true,
              meta: { requiresAuth: true, layoutContainsAds: false }
            },
            {
              name: 'audiocall',
              path: '/calling/',
              component: Calling,
              props: true,
              meta: { requiresAuth: true, layoutContainsAds: false }
            }
            // {
            //   name: 'videoDiagnostics',
            //   path: '/calling/diagnostics',
            //   children: [],
            //   component: VideoDiagnostics,
            //   props: true,
            //   meta: {
            //     requiresAuth: true,
            //     layoutContainsAds: false
            //   },
            // }
          ]
        }
      ]
    },
    {
      path: '/calendar-connect',
      redirect: () => {
        const state = localStorage.getItem('oauth_state')
        const returnRouteName = state ? JSON.parse(state).returnRouteName : ''
        return { name: returnRouteName || 'sharedCalendarTab' }
      }
    },
    {
      path: '/postMatch',
      component: PostMatchLayout,
      beforeEnter: async () =>
        await loadLocales('accountSettings', 'postMatch'),
      props: true,
      meta: {
        requiresAuth: true,
        trackingName: 'postMatch',
        requiresMatched: true
      },
      children: [
        {
          name: 'postMatch',
          path: '/postMatch/',
          component: PostMatch,
          props: true,
          meta: { requiresAuth: false }
        }
      ]
    },
    {
      name: 'shareVaultMain',
      path: '/shareVault',
      component: LandingPageLayout,
      props: true,
      meta: { requiresAuth: false, trackingName: br.appActionFeature.vault },
      children: [
        {
          name: 'shareVault',
          path: '/shareVault/',
          component: ShareVault,
          props: true,
          meta: { requiresAuth: false }
        }
      ]
    },
    {
      name: 'pdf-download',
      path: '/pdf-download',
      component: LandingPageLayout,
      props: true,
      meta: { requiresAuth: false },
      children: [
        {
          name: 'PdfDownload',
          path: '/pdf-download',
          component: PdfDownload,
          props: true,
          meta: { requiresAuth: false }
        }
      ]
    },
    getFeeWaiverRoutes(),
    {
      name: 'newsletter',
      path: '/newsletter',
      component: LandingPageLayout,
      children: [
        {
          name: 'subscribe',
          path: '/newsletter/subscribe',
          component: NewsletterSubscribe
        },
        {
          name: 'welcome',
          path: '/newsletter/welcome',
          component: NewsletterWelcome
        }
      ]
    },
    {
      name: 'NotFound',
      path: '/:pathMatch(.*)*',
      component: NotFoundLayout,
      children: [
        {
          name: 'notFoundPage',
          path: '/:pathMatch(.*)*',
          component: NotFound,
          meta: { requiresAuth: false, layoutContainsAds: false }
        }
      ]
    }
  ],
  scrollBehavior(to, from, savedPosition) {
    if (from.name == 'viewMessage' && to.name == 'messages') return { top: 0 }
    if (from.name == 'messagesEntry' && to.name == 'messages') return { top: 0 }
    if (!from.path.includes('messages') && to.name == 'messages')
      return { top: 0 }

    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

async function handlePostMatchSubscriptionSuccess(
  to: RouteLocationNormalized
): Promise<RouteLocationRaw | null> {
  const sessionId =
    typeof to.query.session_id === 'string' ? to.query.session_id : undefined

  if (to.query.postMatchSubscription === 'true' && sessionId) {
    const accountSettingsStore = useAccountSettingsStore()
    const commonStore = useCommonStore()

    commonStore.setLoading(true)

    try {
      await accountSettingsStore.orderSuccess(sessionId)
      await refreshFullUserInfoForPostMatchSubscription(commonStore)
      commonStore.setWelcomePageViewed('messages', false)
      sessionStorage.setItem('postMatchSubscriptionSuccess', 'true')
      await loadLocales('accountSettings')
    } finally {
      commonStore.setLoading(false)
    }

    return { name: 'messages' }
  }

  return null
}

async function refreshFullUserInfoForPostMatchSubscription(
  commonStore: ReturnType<typeof useCommonStore>
): Promise<void> {
  const maxAttempts = 3

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const fetched = await commonStore.fetchFullUserInfo()

    if (fetched && !commonStore.isRestricted) {
      return
    }

    if (attempt < maxAttempts) {
      await waitForPostMatchSubscriptionRefresh()
    }
  }
}

function waitForPostMatchSubscriptionRefresh(): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, 1000))
}

router.beforeEach(async (to, _from, next) => {
  // check if user is authenticated and that the route requires authentication before routing
  // check if token is being provided in the querystring
  // also check if user is matched and route requires a motch
  //const token = sessionStorage.getItem('user-token')
  const lStore = useLoginStore()
  let token: string | (string | null)[] = lStore.$state.authToken

  if (to.query.z) {
    // parse token to validate
    const _token = to.query.z
    if (typeof _token === 'string') {
      const jwtObj = _token ? decodeJwtService.decodeJwt(_token) : null
      if (jwtObj && jwtObj.UserID) {
        token = _token && _token.length > 0 ? _token : ''
        if (typeof _token === 'string') {
          lStore.setAuthTokenMethod(token)
          lStore.setMfaCookieMethod('')
        }
      }
    }
  }

  if (to.query.x) {
    // parse token to validate
    const _token = to.query.x
    token = _token && _token.length > 0 ? _token : ''
    const payload = { webToggleToken: _token.toString() }
    lStore.webToggleLogin(payload).then(() => {
      next({ path: to.path })
    })
  } else if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    (!token || token == '')
  ) {
    next(getLoginRoute(to.fullPath))
  } else {
    window.popStateDetected = false
    const lcommonStore = useCommonStore()

    if (localStorage.getItem('newVersionAvailable') === 'true') {
      // Store the intended route so we can navigate to it after reload
      sessionStorage.setItem('pendingRoute', to.fullPath)
      // Import the service worker update function dynamically to avoid circular imports
      import('@/services/serviceWorkerService').then(
        ({ triggerServiceWorkerUpdate }) => {
          console.log(
            'Router: New version available, triggering update on route change to:',
            to.path
          )
          triggerServiceWorkerUpdate()
            .then(() => {
              console.log('Service Worker: Update completed successfully')
              // The service worker update will trigger a reload automatically
            })
            .catch((error) => {
              console.error('Service Worker: Update failed:', error)
              // Fallback to manual navigation if update fails
              window.location.assign(to.path)
            })
        }
      )

      // Prevent the route navigation since we're updating the service worker
      next(false)
    } else {
      try {
        const locale = i18nHelpers.getGuessDefaultLocale()

        if (lcommonStore.userLocale !== locale) {
          lcommonStore.setUserLocale(locale)
          Settings.defaultLocale = locale
        }

        if (i18nHelpers.currentLocale !== locale) {
          await i18nHelpers.switchLanguage(locale)
          Settings.defaultLocale = locale
        }
      } catch (error) {
        console.error(
          'Failed to initialize locale from router. Falling back to "en".',
          error
        )

        lcommonStore.setUserLocale(i18nHelpers.defaultLocale)
        Settings.defaultLocale = i18nHelpers.defaultLocale

        try {
          await i18nHelpers.switchLanguage(i18nHelpers.defaultLocale)
          Settings.defaultLocale = i18nHelpers.defaultLocale
        } catch (fallbackError) {
          console.error(
            'Failed to restore the English fallback language from router.',
            fallbackError
          )
        }
      }

      const matchedWithSection = to.matched.find(
        (record) =>
          record.meta && (record.meta as { section?: AppSection }).section
      )

      const checkoutRoute = await handlePostMatchSubscriptionSuccess(to)

      if (checkoutRoute) {
        next(checkoutRoute)
        return
      }

      if (
        matchedWithSection &&
        (matchedWithSection.meta as { section?: AppSection }).section
      ) {
        if (!lcommonStore.fullUserInfo?.userId) {
          const hasUserInfo = await lcommonStore.fetchFullUserInfo()

          if (!hasUserInfo || !lcommonStore.fullUserInfo?.userId) {
            next({ name: 'logout' })
            return
          }

          // Re-evaluate locale after user info loads, since tag-based
          // feature flags (e.g. web_spanish_version: "qa") can now resolve
          const updatedLocale = i18nHelpers.getGuessDefaultLocale()
          if (
            updatedLocale !== i18nHelpers.currentLocale ||
            updatedLocale !== lcommonStore.userLocale
          ) {
            lcommonStore.setUserLocale(updatedLocale)
            Settings.defaultLocale = updatedLocale
            await i18nHelpers.switchLanguage(updatedLocale)
          }
        }

        const section = (matchedWithSection.meta as { section?: AppSection })
          .section as AppSection
        let hasActivity: boolean | undefined

        if (section === 'records') {
          await lcommonStore.fetchActivityCounts()
          hasActivity = hasUserActivity(lcommonStore.activityCounts)
        }

        const isAllowed = isSectionAllowedForUser(section, {
          isRestricted: Boolean(lcommonStore.isRestricted),
          hasActivity
        })

        if (!isAllowed) {
          next(getRestrictedPaywallRoute())
          return
        }
      }

      next()
    }
  }
})

export default router
